import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { ApiError, apiClient } from './client';
import { ChatReplySchema, ChatThreadSchema } from './process-maps';

/**
 * Discovery frameworks (T-0184, backed by the T-0170 API in
 * `apps/api/src/routes/discovery-frameworks.ts`). Firm settings →
 * Discovery frameworks reads and curates the firm's discovery method.
 *
 *   - Any firm user reads published versions; `can_curate` says whether the
 *     caller holds the template curation permission (Decision 54.1).
 *   - A framework has at most one draft. Every draft write carries
 *     `base_revision`, the `draft_revision` last seen; a stale one is refused
 *     with `framework.draft_changed`, never silently applied (rule 2).
 *   - Codes are assigned by the server and never sent (rule 1).
 */

const BASE = '/api/v1/discovery-frameworks';

const PersonSchema = z.object({ id: z.string(), name: z.string().nullable() }).nullable();
const StatusSchema = z.enum(['active', 'retired']);
/** How the draft changed an item, against the current version (draft reads only). */
const ChangeSchema = z.enum(['added', 'changed', 'retired']).nullable().optional();
export type ItemChange = z.infer<typeof ChangeSchema>;

const DraftRefSchema = z.object({
  version_id: z.string(),
  draft_revision: z.number().int(),
  based_on_version: z.number().int().nullable().optional(),
  created_by: PersonSchema.optional(),
  created_at: z.string().optional(),
});

export const FrameworkSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string(),
  current_version: z.number().int(),
  published_at: z.string().nullable(),
  published_by: PersonSchema,
  sources_required: z.number().int().nullable(),
  corroboration_off: z.boolean(),
  engagements_using: z.number().int(),
  counts: z.object({
    areas: z.number().int(),
    interview_types: z.number().int(),
    questionnaires: z.number().int(),
    questions: z.number().int(),
    questionnaire_questions: z.number().int().optional(),
  }),
  /** Curators only. */
  draft: DraftRefSchema.nullable().optional(),
});
export type FrameworkSummary = z.infer<typeof FrameworkSummarySchema>;

export const FrameworkListSchema = z.object({
  can_curate: z.boolean(),
  frameworks: z.array(FrameworkSummarySchema),
});
export type FrameworkList = z.infer<typeof FrameworkListSchema>;

const AreaSchema = z.object({
  id: z.string(),
  code: z.string(),
  label: z.string(),
  name: z.string(),
  definition: z.string(),
  library_module_ids: z.array(z.string()),
  library_modules: z.array(
    z.object({ id: z.string(), name: z.string().nullable(), status: z.string().nullable() }),
  ),
  status: StatusSchema,
  sort_order: z.number(),
  change: ChangeSchema,
});
export type FrameworkArea = z.infer<typeof AreaSchema>;

const InterviewTypeSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  title_keywords: z.array(z.string()),
  area_ids: z.array(z.string()),
  questionnaire_id: z.string().nullable(),
  duration_minutes: z.number().int(),
  status: StatusSchema,
  sort_order: z.number(),
  change: ChangeSchema,
});
export type FrameworkInterviewType = z.infer<typeof InterviewTypeSchema>;

const QuestionnaireQuestionSchema = z.object({
  id: z.string(),
  code: z.string(),
  prompt: z.string(),
  answer_type: z.string(),
  options: z.array(z.string()),
  area_ids: z.array(z.string()),
  status: StatusSchema,
  sort_order: z.number(),
  change: ChangeSchema,
});
export type QuestionnaireQuestion = z.infer<typeof QuestionnaireQuestionSchema>;

const QuestionnaireSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  audience: z.string(),
  minutes: z.number().int(),
  one_per_department: z.boolean(),
  status: StatusSchema,
  sort_order: z.number(),
  change: ChangeSchema,
  questions: z.array(QuestionnaireQuestionSchema),
});
export type FrameworkQuestionnaire = z.infer<typeof QuestionnaireSchema>;

/** An area question, or an interview type's question or on-screen ask. */
const ListQuestionSchema = z.object({
  id: z.string(),
  code: z.string(),
  kind: z.enum(['question', 'on_screen']).optional(),
  text: z.string(),
  status: StatusSchema,
  sort_order: z.number(),
  change: ChangeSchema,
});
export type ListQuestion = z.infer<typeof ListQuestionSchema>;

const ProcessSchema = z.object({
  id: z.string(),
  area_id: z.string(),
  name: z.string(),
  status: StatusSchema,
  sort_order: z.number(),
  has_reference_map: z.boolean(),
  /** The reference process map: lanes, steps and flows, no citations (E12-F01 §2). */
  reference_map: z.unknown().nullable().optional(),
  change: ChangeSchema,
});
export type FrameworkProcess = z.infer<typeof ProcessSchema>;

const PlanRoleSchema = z.object({
  ordinal: z.number().int().optional(),
  role: z.string(),
  optional: z.boolean(),
});
export type PlanRole = z.infer<typeof PlanRoleSchema>;

const ChangeCountSchema = z.object({
  added: z.number().int(),
  changed: z.number().int(),
  retired: z.number().int(),
});
export type DiffKind = 'areas' | 'interview_types' | 'questionnaires' | 'questions' | 'processes';

const VersionInfoSchema = z.object({
  id: z.string(),
  state: z.enum(['draft', 'published']),
  version: z.number().int().nullable(),
  based_on_version: z.number().int().nullable(),
  sources_required: z.number().int(),
  corroboration_off: z.boolean(),
  draft_revision: z.number().int().optional(),
  change_note: z.string().nullable(),
  published_at: z.string().nullable(),
  published_by: PersonSchema,
  created_at: z.string(),
  created_by: PersonSchema,
  has_changes: z.boolean().optional(),
  changes: z.record(ChangeCountSchema).optional(),
});

export const FrameworkContentSchema = z.object({
  framework: z.object({
    id: z.string(),
    name: z.string(),
    status: z.string(),
    current_version: z.number().int(),
  }),
  can_curate: z.boolean(),
  version: VersionInfoSchema.nullable(),
  areas: z.array(AreaSchema),
  interview_types: z.array(InterviewTypeSchema),
  questionnaires: z.array(QuestionnaireSchema),
  area_questions: z.record(z.array(ListQuestionSchema)),
  type_questions: z.record(z.array(ListQuestionSchema)),
  processes: z.array(ProcessSchema),
  plan_roles: z.record(z.array(PlanRoleSchema)),
});
export type FrameworkContent = z.infer<typeof FrameworkContentSchema>;

export const FrameworkVersionsSchema = z.object({
  versions: z.array(
    z.object({
      version: z.number().int(),
      change_note: z.string().nullable(),
      published_by: PersonSchema,
      published_at: z.string().nullable(),
      sources_required: z.number().int(),
      is_current: z.boolean(),
    }),
  ),
});
export type FrameworkVersions = z.infer<typeof FrameworkVersionsSchema>;

/** The respondent view (E12-F08 §3 session shape), nothing sent. */
export const QuestionnairePreviewSchema = z.object({
  client_name: z.string().nullable(),
  version: z.object({ state: z.string(), version: z.number().int().nullable() }),
  questionnaire: z.object({ id: z.string(), code: z.string() }),
  payload: z.object({
    title: z.string(),
    audience_line: z.string(),
    minutes: z.number().int(),
    one_per_department: z.boolean(),
    questions: z.array(
      z.object({
        ordinal: z.number().int(),
        prompt: z.string(),
        answer_type: z.string(),
        options: z.array(z.string()),
      }),
    ),
  }),
});
export type QuestionnairePreview = z.infer<typeof QuestionnairePreviewSchema>;

/** Every draft write answers with the new revision; adds also with the new id and code. */
const DraftWriteSchema = z.object({
  draft_revision: z.number().int(),
  changed: z.boolean().optional(),
  code: z.string().optional(),
  area_id: z.string().optional(),
  question_id: z.string().optional(),
  interview_type_id: z.string().optional(),
  questionnaire_id: z.string().optional(),
  process_id: z.string().optional(),
});
export type DraftWriteResult = z.infer<typeof DraftWriteSchema>;

const PublishResultSchema = z.object({
  version: z.number().int(),
  corroboration_off: z.boolean(),
});
export type PublishResult = z.infer<typeof PublishResultSchema>;

const CreatedFrameworkSchema = z.object({ id: z.string(), name: z.string() });

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/** `details.reason` of an API error, for example `framework.draft_changed`. */
export function frameworkReason(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const reason = err.details?.['reason'];
  return typeof reason === 'string' ? reason : null;
}

/**
 * The draft the caller worked from is no longer the draft: someone else
 * saved to it first, or it was published or discarded (rule 2).
 */
export function isStaleDraftError(err: unknown): boolean {
  const reason = frameworkReason(err);
  return reason === 'framework.draft_changed' || reason === 'framework.no_draft';
}

export interface ExistingItem {
  id: string;
  code: string | null;
  name: string;
  status: string;
}

/** The item a `framework.name_taken` refusal names, and whether it can be restored. */
export function nameTakenFromError(
  err: unknown,
): { existing: ExistingItem | null; restorable: boolean } | null {
  if (frameworkReason(err) !== 'framework.name_taken') return null;
  const details = (err as ApiError).details ?? {};
  const parsed = z
    .object({ id: z.string(), code: z.string().nullable(), name: z.string(), status: z.string() })
    .safeParse(details['existing']);
  return {
    existing: parsed.success ? parsed.data : null,
    restorable: details['restorable'] === true,
  };
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export type ContentMode = 'current' | 'draft';

const fw = (id: string) => `${BASE}/${encodeURIComponent(id)}`;

export function getFrameworks(): Promise<FrameworkList> {
  return apiClient(BASE, FrameworkListSchema);
}

export function getFrameworkContent(id: string, mode: ContentMode): Promise<FrameworkContent> {
  return apiClient(fw(id), FrameworkContentSchema, {
    query: { version: mode === 'draft' ? 'draft' : undefined },
  });
}

export function getFrameworkVersions(id: string): Promise<FrameworkVersions> {
  return apiClient(`${fw(id)}/versions`, FrameworkVersionsSchema);
}

export function getQuestionnairePreview(
  id: string,
  questionnaireId: string,
  mode: ContentMode,
): Promise<QuestionnairePreview> {
  return apiClient(
    `${fw(id)}/questionnaires/${encodeURIComponent(questionnaireId)}/preview`,
    QuestionnairePreviewSchema,
    { query: { version: mode === 'draft' ? 'draft' : undefined } },
  );
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

function newIdempotencyKey(): string {
  return typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function write(
  method: 'POST' | 'PATCH' | 'PUT',
  path: string,
  base: number,
  body: Record<string, unknown>,
): Promise<DraftWriteResult> {
  return apiClient(path, DraftWriteSchema, {
    method,
    body: { ...body, base_revision: base },
    idempotencyKey: newIdempotencyKey(),
  });
}

export interface AreaPatch {
  label?: string;
  name?: string;
  definition?: string;
  library_module_ids?: string[];
  status?: 'active' | 'retired';
}
export interface QuestionPatch {
  text?: string;
  prompt?: string;
  answer_type?: string;
  options?: string[];
  area_ids?: string[];
  status?: 'active' | 'retired';
}
export interface InterviewTypePatch {
  name?: string;
  title_keywords?: string[];
  area_ids?: string[];
  questionnaire_id?: string | null;
  duration_minutes?: number;
  status?: 'active' | 'retired';
}
export interface QuestionnairePatch {
  name?: string;
  audience?: string;
  minutes?: number;
  one_per_department?: boolean;
  status?: 'active' | 'retired';
}

/**
 * Draft writes. Each takes the framework id and the `base_revision`, which
 * the screen reads from the draft it last loaded.
 */
export const draftApi = {
  setRules: (id: string, base: number, sources: number) =>
    write('PATCH', `${fw(id)}/draft/rules`, base, { sources_required: sources }),
  addArea: (id: string, base: number, label: string) =>
    write('POST', `${fw(id)}/draft/areas`, base, { label, name: label }),
  patchArea: (id: string, base: number, areaId: string, patch: AreaPatch) =>
    write('PATCH', `${fw(id)}/draft/areas/${encodeURIComponent(areaId)}`, base, { ...patch }),
  addAreaQuestion: (id: string, base: number, areaId: string, text: string) =>
    write('POST', `${fw(id)}/draft/areas/${encodeURIComponent(areaId)}/questions`, base, { text }),
  patchQuestion: (id: string, base: number, questionId: string, patch: QuestionPatch) =>
    write('PATCH', `${fw(id)}/draft/questions/${encodeURIComponent(questionId)}`, base, {
      ...patch,
    }),
  addInterviewType: (id: string, base: number, name: string) =>
    write('POST', `${fw(id)}/draft/interview-types`, base, { name }),
  patchInterviewType: (id: string, base: number, typeId: string, patch: InterviewTypePatch) =>
    write('PATCH', `${fw(id)}/draft/interview-types/${encodeURIComponent(typeId)}`, base, {
      ...patch,
    }),
  addTypeQuestion: (
    id: string,
    base: number,
    typeId: string,
    kind: 'question' | 'on_screen',
    text: string,
  ) =>
    write('POST', `${fw(id)}/draft/interview-types/${encodeURIComponent(typeId)}/questions`, base, {
      kind,
      text,
    }),
  putPlanRoles: (
    id: string,
    base: number,
    typeId: string,
    roles: Array<{ role: string; optional: boolean }>,
  ) =>
    write('PUT', `${fw(id)}/draft/interview-types/${encodeURIComponent(typeId)}/plan-roles`, base, {
      roles,
    }),
  addQuestionnaire: (id: string, base: number, name: string) =>
    write('POST', `${fw(id)}/draft/questionnaires`, base, { name }),
  patchQuestionnaire: (
    id: string,
    base: number,
    questionnaireId: string,
    patch: QuestionnairePatch,
  ) =>
    write('PATCH', `${fw(id)}/draft/questionnaires/${encodeURIComponent(questionnaireId)}`, base, {
      ...patch,
    }),
  addQuestionnaireQuestion: (id: string, base: number, questionnaireId: string, prompt: string) =>
    write(
      'POST',
      `${fw(id)}/draft/questionnaires/${encodeURIComponent(questionnaireId)}/questions`,
      base,
      { prompt, answer_type: 'text', area_ids: [] },
    ),
  addProcess: (id: string, base: number, areaId: string, name: string) =>
    write('POST', `${fw(id)}/draft/processes`, base, { area_id: areaId, name }),
  patchProcess: (id: string, base: number, processId: string, status: 'active' | 'retired') =>
    write('PATCH', `${fw(id)}/draft/processes/${encodeURIComponent(processId)}`, base, { status }),
};

// ---------------------------------------------------------------------------
// Reference process maps (T-0189): change sets and chat on the draft
// ---------------------------------------------------------------------------

const ReferenceProposalSchema = DraftWriteSchema.extend({
  change_set_id: z.string(),
  valid: z.boolean(),
  invalid_reason: z.string().nullable().optional(),
  operation_index: z.number().nullable().optional(),
  check: z.string().nullable().optional(),
});
export type ReferenceProposal = z.infer<typeof ReferenceProposalSchema>;

const refProcess = (id: string, processId: string) =>
  `${fw(id)}/draft/processes/${encodeURIComponent(processId)}`;

export const referenceMapApi = {
  /** A change set from the canvas, checked by code against the draft's map. Nothing changes yet. */
  propose: (
    id: string,
    base: number,
    processId: string,
    operations: ReadonlyArray<Record<string, unknown>>,
  ): Promise<ReferenceProposal> =>
    apiClient(`${refProcess(id, processId)}/reference-map/change-sets`, ReferenceProposalSchema, {
      method: 'POST',
      body: { operations, origin: 'canvas', base_revision: base },
      idempotencyKey: newIdempotencyKey(),
    }),
  /** Writes the draft's reference map from the change set (chat or canvas). */
  apply: (id: string, base: number, processId: string, changeSetId: string) =>
    write(
      'POST',
      `${refProcess(id, processId)}/reference-map/change-sets/${encodeURIComponent(changeSetId)}:apply`,
      base,
      {},
    ),
  discard: (id: string, processId: string, changeSetId: string) =>
    apiClient(
      `${refProcess(id, processId)}/reference-map/change-sets/${encodeURIComponent(changeSetId)}:discard`,
      z.object({ status: z.string() }),
      { method: 'POST', idempotencyKey: newIdempotencyKey() },
    ),
  chat: (id: string, processId: string) =>
    apiClient(`${refProcess(id, processId)}/chat`, ChatThreadSchema),
  /** Billed to the firm's AI budget, work class chat (Decision 54.2). */
  send: (id: string, base: number, processId: string, text: string) =>
    apiClient(`${refProcess(id, processId)}/chat/messages`, ChatReplySchema, {
      method: 'POST',
      body: { text, base_revision: base },
      idempotencyKey: newIdempotencyKey(),
    }),
};

export const referenceChatKey = (id: string, processId: string) =>
  ['discovery-frameworks', 'reference-chat', id, processId] as const;

export function useReferenceChatQuery(id: string, processId: string, enabled: boolean) {
  return useQuery({
    queryKey: referenceChatKey(id, processId),
    queryFn: () => referenceMapApi.chat(id, processId),
    enabled,
    staleTime: 5_000,
    retry: false,
  });
}

/** Start the one draft (rule 2). `framework.draft_exists` means: join it. */
export function startDraft(id: string): Promise<unknown> {
  return apiClient(`${fw(id)}/draft`, z.unknown(), {
    method: 'POST',
    idempotencyKey: newIdempotencyKey(),
  });
}

export function discardDraft(id: string, base: number): Promise<unknown> {
  return apiClient(`${fw(id)}/draft`, z.unknown(), {
    method: 'DELETE',
    query: { base_revision: base },
    idempotencyKey: newIdempotencyKey(),
  });
}

export function publishFramework(id: string, base: number, changeNote: string) {
  return apiClient(`${fw(id)}:publish`, PublishResultSchema, {
    method: 'POST',
    body: { change_note: changeNote, base_revision: base },
    idempotencyKey: newIdempotencyKey(),
  });
}

/** A new framework, its version 1 a draft copied from another framework. */
export function createFramework(name: string, copyFromId: string) {
  return apiClient(BASE, CreatedFrameworkSchema, {
    method: 'POST',
    body: { name, copy_from_framework_id: copyFromId },
    idempotencyKey: newIdempotencyKey(),
  });
}

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

export const discoveryFrameworkKeys = {
  all: ['discovery-frameworks'] as const,
  list: () => ['discovery-frameworks', 'list'] as const,
  content: (id: string, mode: ContentMode) =>
    ['discovery-frameworks', 'content', id, mode] as const,
  versions: (id: string) => ['discovery-frameworks', 'versions', id] as const,
  preview: (id: string, questionnaireId: string, mode: ContentMode) =>
    ['discovery-frameworks', 'preview', id, questionnaireId, mode] as const,
};

export function useFrameworksQuery() {
  return useQuery({
    queryKey: discoveryFrameworkKeys.list(),
    queryFn: getFrameworks,
    staleTime: 30_000,
  });
}

export function useFrameworkContentQuery(id: string | null, mode: ContentMode) {
  return useQuery({
    queryKey: discoveryFrameworkKeys.content(id ?? '', mode),
    queryFn: () => getFrameworkContent(id!, mode),
    enabled: Boolean(id),
    // Another curator may be editing the same draft.
    staleTime: mode === 'draft' ? 0 : 30_000,
    // Opening the draft keeps the published content on screen until it loads,
    // but never shows one framework's content under another's name.
    placeholderData: (prev) => (prev?.framework.id === id ? prev : undefined),
  });
}

export function useFrameworkVersionsQuery(id: string | null) {
  return useQuery({
    queryKey: discoveryFrameworkKeys.versions(id ?? ''),
    queryFn: () => getFrameworkVersions(id!),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useQuestionnairePreviewQuery(
  target: { frameworkId: string; questionnaireId: string; mode: ContentMode } | null,
) {
  return useQuery({
    queryKey: discoveryFrameworkKeys.preview(
      target?.frameworkId ?? '',
      target?.questionnaireId ?? '',
      target?.mode ?? 'current',
    ),
    queryFn: () =>
      getQuestionnairePreview(target!.frameworkId, target!.questionnaireId, target!.mode),
    enabled: target != null,
    staleTime: 0,
  });
}
